import client from './providers'
import { axios } from '@argon/app/axios'

const NAMESPACE = 'realtime'

const uid = () => (performance.now().toString(36) + Math.random().toString(36)).replace(/\./g, '')

const state = {
  enabled: process.env.VUE_APP_FIRESTORE_API_KEY ? process.env.VUE_APP_FIRESTORE_API_KEY !== 'false' : false,
  authenticated: false,
  listeners: [],
  docs: {}
}

const mutations = {
  SET_LISTENERS: (state, payload) => {
    state.listeners = payload
  },
  SET_AUTHENTICATED: (state, payload) => {
    state.authenticated = payload
  },
  SET_DOCS: (state, payload) => {
    state.docs = payload
  }
}
const actions = {
  connectToRemote: ({ commit, dispatch, state }) => {
    return new Promise((resolve, reject) => {
      if (!state.enabled) {
        return resolve(true)
      }
      if (state.authenticated) {
        return resolve(true)
      }
      axios({ url: '/realtime-auth', method: 'GET' })
        .then(({ data }) => {
          return client.signIn(data.token)
        })
        .then(() => {
          commit('SET_AUTHENTICATED', true)
          let snack = { string: 'realtimeLoginSuccess', params: {} }
          dispatch('app/setSnackMessage', snack, { root: true })
          resolve(true)
        })
        .catch((error) => {
          commit('SET_AUTHENTICATED', false)
          let snack = { string: 'realtimeLoginFailed', params: {} }
          dispatch('app/setSnackMessage', snack, { root: true })
          reject(error)
        })
    })
  },

  disconnectFromRemote: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      if (!state.enabled) {
        return resolve(true)
      }
      dispatch('clearListeners').then(() => {
        client.signOut()
        commit('SET_AUTHENTICATED', false)
        resolve(true)
      })
    })
  },

  logToSnack: ({ dispatch, rootGetters }, payload) => {
    const { category, doc } = payload
    const data = doc.data()
    // Disable snack if the user owns the change
    let profile = rootGetters['me/profile']
    if (!data) {
      return
    }
    if (data.refid && data.refid === profile.refid) {
      return
    }
    let snack = {
      string: data.display ? `${category}UpdatedBy` : `${category}Updated`,
      params: data.display ? { display: data.display } : {}
    }
    dispatch('app/setSnackMessage', snack, { root: true })
  },

  buildClientCallback: ({ commit, dispatch, state }, payload) => {
    return new Promise((resolve, reject) => {
      const { category, identifier } = payload
      const clientCallback = (doc) => {
        if (!doc.exists) {
          return // nothing to do here
        }
        const val = { ...state.docs[category], [identifier]: uid() }
        const docs = { ...state.docs, [category]: val }
        commit('SET_DOCS', docs)
        dispatch('logToSnack', { category, doc })
      }
      resolve(clientCallback)
    })
  },

  removeListener: ({ commit, state, getters }, { category, identifier }) => {
    return new Promise((resolve, reject) => {
      let match = getters.listenerByCategoryAndID(category, identifier)
      if (!match) {
        return resolve(true)
      }
      match.unsubscriber()
      let listeners = state.listeners.filter((x) => x.identifier !== identifier)
      commit('SET_LISTENERS', listeners)
      try {
        const { [category]: value } = state.docs
        const { [identifier]: key, ...remain } = value
        const docs = { ...state.docs, [category]: remain }
        commit('SET_DOCS', docs)
      } catch (err) {}
      return resolve(true)
    })
  },

  removeListenerCategory: ({ commit, state, getters }, category) => {
    return new Promise((resolve, reject) => {
      if (!state.enabled) {
        return resolve(true)
      }
      let matches = getters.listenerByCategory(category)
      if (!matches) {
        return resolve(true)
      }
      matches.forEach((x) => x.unsubscriber())
      let listeners = state.listeners.filter((x) => x.category !== category)
      commit('SET_LISTENERS', listeners)
      const { [category]: value, ...docs } = state.docs
      commit('SET_DOCS', docs)
      return resolve(true)
    })
  },

  clearListeners: ({ commit, state }) => {
    return new Promise((resolve, reject) => {
      if (!state.enabled) {
        return resolve(true)
      }
      state.listeners.forEach((listener) => {
        listener.unsubscriber()
      })
      commit('SET_LISTENERS', [])
      return resolve(true)
    })
  },

  addListener: ({ commit, dispatch, state, getters, rootGetters }, payload) => {
    const { category, identifier } = payload
    let profile = rootGetters['me/profile']
    return new Promise((resolve, reject) => {
      if (!state.enabled) {
        return resolve(true)
      }
      let idMatch = getters.listenerByCategoryAndID(category, identifier)
      if (idMatch) {
        return resolve(true)
      }

      dispatch('connectToRemote')
        .then(() => {
          return dispatch('buildClientCallback', payload)
        })
        .then((callback) => {
          return client.listen(category, identifier, callback, profile)
        })
        .then((unsubscriber) => {
          let listeners = [
            ...state.listeners,
            {
              identifier,
              category,
              unsubscriber,
              setup: false
            }
          ]
          commit('SET_LISTENERS', listeners)
          resolve(true)
        })
        .catch(reject)
    })
  }
}

const getters = {
  listenerByCategory: (state) => (category) => {
    return state.listeners.filter((x) => x.category === category)
  },
  listenerByCategoryAndID: (state) => (category, identifier) => {
    return state.listeners.find((x) => x.category === category && x.identifier === identifier)
  },
  realtimeKey:
    (state) =>
    ({ category, identifier }) =>
      state.docs[category] ? state.docs[category][identifier] : null
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}

export { NAMESPACE }
